import { createContext, useContext, useMemo } from 'react';

import { useCdnSessionCookie } from './hooks/useCdnSessionCookie';
import { useCloudflare } from './hooks/useCloudflare';
import { usePageUnload } from './hooks/usePageUnload';
import { useSessionStorage } from './hooks/useSessionStorage';
import { useSessionUser } from './hooks/useSessionUser';
import { SESSION_STATE } from './sessionProvider.constants';
import { ISessionContext, ISessionProvider } from './sessionProvider.types';

const SessionContext = createContext<ISessionContext>({} as ISessionContext);

export const useSession = (): ISessionContext => useContext(SessionContext);

const SessionProvider = ({ children }: ISessionProvider): JSX.Element => {
  const {
    firebaseId,
    hasInstitutionAccessScope,
    loading,
    sessionState,
    user,
    setFirebaseId,
    setSessionState,
    setUser,
    isUploading,
    setIsUploading,
  } = useSessionUser();

  const {
    getExpiresIn,
    updateExpiresIn,
    updateFirebaseId,
    clearSessionFirebase,
    clearSessionUser,
  } = useSessionStorage({
    setFirebaseId,
    setUser,
  });

  const { isCloudflareAutoLoginEnabled, toggleCloudflareAutoLogin } =
    useCloudflare({ firebaseId });

  // Get/revalidate CDN cookie
  useCdnSessionCookie(firebaseId, sessionState === SESSION_STATE.AUTHENTICATED);

  // Clean up on page unload
  usePageUnload({ sessionState, clearSessionFirebase });

  // user context values
  const values = useMemo<ISessionContext>(
    () => ({
      isUploading,
      setIsUploading,
      user,
      hasInstitutionAccessScope,
      setUser,
      getExpiresIn,
      updateExpiresIn,
      firebaseId,
      updateFirebaseId,
      clearSessionUser,
      clearSessionFirebase,
      loading,
      isCloudflareAutoLoginEnabled,
      toggleCloudflareAutoLogin,
      sessionState,
      setSessionState,
    }),
    [
      isUploading,
      setIsUploading,
      user,
      hasInstitutionAccessScope,
      setUser,
      getExpiresIn,
      updateExpiresIn,
      firebaseId,
      updateFirebaseId,
      clearSessionUser,
      clearSessionFirebase,
      loading,
      isCloudflareAutoLoginEnabled,
      toggleCloudflareAutoLogin,
      sessionState,
      setSessionState,
    ],
  );

  return (
    <SessionContext.Provider value={values}>{children}</SessionContext.Provider>
  );
};

export default SessionProvider;
